/**
 * Connection status and the boot greeting (spec ST-02).
 *
 * Both specs make the greeting a function of connection state, but they do not
 * agree on the state set, so this resolves per host:
 *
 *   Outlook — depends on two services, and "the greeting distinguishes all four
 *   combinations — both connected, AI only, MS365 only, neither — each naming
 *   exactly what to connect in Settings".
 *
 *   Word — "depends on one service (the AI) for chat plus optional MS365 for
 *   DP-05, and the boot greeting differs across three states": AI connected with
 *   document content, AI connected with an empty document, AI not connected.
 *
 *   PowerPoint — connected, it gets its own Figure 3.1-style greeting (a
 *   time-of-day salutation plus the deck-editing pitch). Every other
 *   combination, and every other host, falls back to the neutral variant: the
 *   same "what to connect" guidance without Outlook's mailbox nouns.
 *
 * The dot is derived per host too — Word's chat is dead without the AI service,
 * so AI-down is offline there, where Outlook still has MS365 features and reads
 * degraded. Tooltips name the failing service and are host-independent.
 *
 * The remedies are NOT the specs' "connect it in Settings": in production
 * Microsoft 365 is the MSAL Graph token and the AI provider is an admin-managed
 * Studio connector, so neither is a Settings row a user can toggle. See
 * M365_REMEDY / AI_REMEDY.
 */

import type { Channel } from "../host";
import type { MailboxSnapshot } from "../../integrations/email/hooks/useMailboxSnapshot";

export type ConnectionDot = "online" | "degraded" | "offline" | "unknown";

/**
 * Which surface the pane is running on, for the greeting copy only — the dot and
 * tooltip are identical on both. Word's ST-02 names the assistant for the
 * document, not the mailbox (Figure 3.2), and what a Word user loses when Graph
 * is down is DP-05 (/paste-email), not "analyse your emails and calendar".
 */
export type ConnectionSurface = "mailbox" | "document";

export interface ConnectionState {
  readonly dot: ConnectionDot;
  /** Tooltip and aria-label. Names the failing service so the dot is self-explaining. */
  readonly tooltip: string;
  /** The boot greeting. Null only before the load settles, and for hosts with
   *  no documented greeting when everything is connected — the caller's host
   *  hint stands there. */
  readonly greeting: string | null;
}

/** Word's document figures, straight off WordDocumentContext. */
export interface DocumentFacts {
  readonly name: string;
  readonly wordCount: number;
}

export interface ConnectionStateInput {
  /** Undefined when no AppConfigProvider is mounted (standalone usage). */
  readonly settingsStatus: "idle" | "loading" | "ready" | "error" | undefined;
  readonly aiConnected: boolean;
  readonly m365Connected: boolean;
  /** AIVA-9278: false when the Studio Microsoft Graph connector is what's down, which
   *  swaps the "sign in again" remedy for an administrator one. Absent means connected. */
  readonly m365ConnectorConnected?: boolean;
  /** False while offline; the dot then reads both services down. Absent means reachable. */
  readonly networkReachable?: boolean;
  /** First token is used for the salutation; omitted when there is no name. */
  readonly displayName?: string | null;
  /** Which host's greeting to build. Absent behaves as the neutral host. */
  readonly host?: Channel | null;
  /** Outlook only — the mailbox/calendar counts the briefing quotes. */
  readonly mailbox?: MailboxSnapshot | null;
  /** Word only — the open document's name and word count. Null while the read
   *  is still in flight, which is NOT the same as an empty document. */
  readonly document?: DocumentFacts | null;
  /** Word only — the document read threw. Also not the same as empty. */
  readonly documentUnavailable?: boolean;
  /** Injectable clock for the time-of-day salutation. */
  readonly now?: Date;
}

function timeOfDay(hours: number): string {
  if (hours < 12) return "Good morning";
  if (hours < 18) return "Good afternoon";
  return "Good evening";
}

function firstNameOf(displayName?: string | null): string {
  return displayName?.trim().split(/\s+/)[0] ?? "";
}

// Microsoft 365 access is the MSAL Graph token, not a Settings connector row, so
// re-authenticating is the only remedy a user can actually act on. Matches the
// wording useChat already uses when a Graph call fails mid-turn.
export const M365_REMEDY = "Please sign in again to reconnect Microsoft 365.";

// AIVA-9278: when the workspace's Microsoft Graph connector is what's down, signing
// in again changes nothing — only an administrator can reconnect it in Studio.
export const M365_ADMIN_REMEDY = "Ask your AiVA Studio administrator to reconnect Microsoft 365.";

function m365Remedy(input: ConnectionStateInput): string {
  return input.m365ConnectorConnected === false ? M365_ADMIN_REMEDY : M365_REMEDY;
}

// The AI provider is an admin-managed Studio connector — nothing the user can fix.
export const AI_REMEDY = "Ask your AiVA Studio administrator to reconnect the AI provider.";

/** Outlook's Figure 3.1 form: "Good morning Mark." — no comma before the name. */
function outlookSalutation(input: ConnectionStateInput): string {
  const name = firstNameOf(input.displayName);
  const nameSuffix = name ? ` ${name}` : "";
  return `${timeOfDay((input.now ?? new Date()).getHours())}${nameSuffix}.`;
}

/**
 * Word's Figure 3.1 form: "Good morning, Mark, I'm AiVA, your …".
 *
 * Rendered as Markdown, so the emphasis in the figure is carried in the string
 * itself — the brand, the document name and each capability's leading phrase.
 */
function wordSalutation(input: ConnectionStateInput): string {
  const name = firstNameOf(input.displayName);
  const salutation = timeOfDay((input.now ?? new Date()).getHours());
  const opener = name ? `${salutation}, ${name},` : `${salutation},`;
  return `${opener} I'm **AiVA**, your intelligent document assistant.`;
}

/** PowerPoint's Figure 3.1 form: "Good morning, Mark, I'm AiVA." */
function powerPointSalutation(input: ConnectionStateInput): string {
  const name = firstNameOf(input.displayName);
  const salutation = timeOfDay((input.now ?? new Date()).getHours());
  const opener = name ? `${salutation}, ${name},` : `${salutation},`;
  return `${opener} I'm **AiVA**.`;
}

function plural(count: number, noun: string): string {
  return `${count.toLocaleString("en-GB")} ${noun}${count === 1 ? "" : "s"}`;
}

const UNKNOWN: ConnectionState = {
  dot: "unknown",
  tooltip: "Status unknown",
  greeting: null,
};

function tooltipFor(host: Channel | null, aiConnected: boolean, m365Connected: boolean): string {
  // "In Word the dot tracks one service: the AI (ST-02)" — so MS365 must not
  // colour the dot or word the tooltip there.
  if (host === "word") return aiConnected ? "Connected" : "AI service unavailable";
  if (aiConnected && m365Connected) return "Connected";
  if (aiConnected) return "Microsoft 365 unavailable";
  if (m365Connected) return "AI service unavailable";
  return "AI service and Microsoft 365 unavailable";
}

function dotFor(host: Channel | null, aiConnected: boolean, m365Connected: boolean): ConnectionDot {
  if (host === "word") return aiConnected ? "online" : "offline";
  if (aiConnected && m365Connected) return "online";
  return aiConnected || m365Connected ? "degraded" : "offline";
}

/**
 * Outlook, all four combinations. The connected briefing is Figure 3.1 copy; it
 * holds back the counts sentence until the Graph snapshot lands rather than
 * quoting a zero it has not verified.
 */
function outlookGreeting(input: ConnectionStateInput): string {
  const salutation = outlookSalutation(input);
  const { aiConnected, m365Connected } = input;

  if (aiConnected && m365Connected) {
    const snapshot = input.mailbox;
    let briefing = "";
    if (snapshot?.status === "ready") {
      // Figure 3.1 follows the counts with "Everything looks in order — no urgent
      // actions needed right now." That is deliberately NOT rendered: nothing at
      // boot ranks urgency, so it would be an unbacked all-clear. It is also the
      // sentence MS-03 warns against — "AiVA has no standing index of the mailbox
      // … Copy must not imply otherwise."
      briefing =
        ` I've analysed your mailbox and calendar (${plural(snapshot.unreadEmails, "unread email")}, ` +
        `${plural(snapshot.eventsToday, "event")} today).`;
    } else if (snapshot?.status === "unavailable") {
      // Reporting the failed read beats dropping the sentence: a silently
      // shortened greeting is indistinguishable from one still loading.
      briefing = " I couldn't read your mailbox and calendar counts just now.";
    }
    return `${salutation}${briefing}\n\nAsk me anything about your emails or schedule.`;
  }

  if (aiConnected) {
    return (
      `${salutation} I'm AiVA, your intelligent email assistant. AI Service is connected, ` +
      `but I need access to your Microsoft 365 mailbox to analyse your emails and calendar. ${m365Remedy(input)}`
    );
  }

  if (m365Connected) {
    return (
      `${salutation} I'm AiVA, your intelligent email assistant. Microsoft 365 is connected, ` +
      `but I need an AI Service connected to analyse your emails and generate actions. ${AI_REMEDY}`
    );
  }

  return (
    `${salutation} I'm AiVA, your intelligent email assistant by Williams Lea. ` +
    `To get started I need both services. ${m365Remedy(input)} ${AI_REMEDY}`
  );
}

/**
 * Word's three states. MS365 branches nothing here — not the greeting and not
 * the dot: the spec ties both to the AI service, and scopes MS365 to DP-05.
 */
function wordGreeting(input: ConnectionStateInput): string {
  const opener = wordSalutation(input);

  if (!input.aiConnected) {
    // Scoped to answering questions, not to helping at all: ST-03 keeps the
    // local document operations working with no AI service, and §1 forbids the
    // add-in claiming it "can't" do something it plainly can.
    return (
      `${opener}\n\nI need an AI Service connected before I can answer questions about ` +
      `this document. ${AI_REMEDY}`
    );
  }

  // A failed read leaves the same empty context behind as an empty document, so
  // without this it would be reported as one — the claim Outlook's `unavailable`
  // branch exists to avoid.
  if (input.documentUnavailable) {
    return `${opener}\n\nI couldn't read this document just now. Ask me anything and I'll try again.`;
  }

  // The document read has not come back yet. Falling through to the empty-document
  // copy here would tell every user their document is empty for a beat — and Word
  // auto-opens the pane, so it happens on every document.
  if (!input.document) return opener;

  const wordCount = input.document.wordCount;
  if (wordCount <= 0) {
    return (
      `${opener}\n\nThe document appears to be empty. Start writing and I can help you ` +
      "with content, formatting, and analysis."
    );
  }

  const name = input.document.name?.trim();
  const words = plural(wordCount, "word");
  const subject = name ? `**${name}** (${words})` : words;
  return (
    `${opener}\n\nI can see ${subject}. I can help you:\n\n` +
    "- **Summarise** key points\n" +
    "- **Review formatting** and consistency\n" +
    "- **Analyse content** and suggest improvements\n" +
    "- **Save to OneDrive** or share via Engage\n\n" +
    "Ask me anything about this document."
  );
}

/** PowerPoint, connected: the deck-editing pitch. Disconnected combinations
 *  have no PowerPoint-specific copy, so they read like the neutral variant. */
function powerPointGreeting(input: ConnectionStateInput): string {
  const opener = powerPointSalutation(input);
  const { aiConnected, m365Connected } = input;

  if (aiConnected && m365Connected) {
    return (
      `${opener}\n\nI can help polish slides in minutes, not hours. Tap an action ` +
      "chip below, type a command, or press Ctrl + Shift + K for every action."
    );
  }

  if (aiConnected) {
    return (
      `${opener} AI Service is connected, but I need access to your Microsoft 365 ` +
      `account. ${m365Remedy(input)}`
    );
  }

  if (m365Connected) {
    return (
      `${opener} Microsoft 365 is connected, but I need an AI Service connected ` +
      `before I can help. ${AI_REMEDY}`
    );
  }

  return `${opener} To get started I need both services. ${m365Remedy(input)} ${AI_REMEDY}`;
}

/** Hosts with no ST-02 greeting of their own (currently just "no host").
 *  Same "name what to connect" rule, without Outlook's mailbox nouns. */
function neutralGreeting(input: ConnectionStateInput): string | null {
  const salutation = outlookSalutation(input);
  const { aiConnected, m365Connected } = input;

  // Nothing to warn about, so the caller's host hint stands.
  if (aiConnected && m365Connected) return null;

  if (aiConnected) {
    return (
      `${salutation} I'm AiVA. AI Service is connected, but I need access to your ` +
      `Microsoft 365 account. ${m365Remedy(input)}`
    );
  }

  if (m365Connected) {
    return (
      `${salutation} I'm AiVA. Microsoft 365 is connected, but I need an AI Service ` +
      `connected before I can help. ${AI_REMEDY}`
    );
  }

  return (
    `${salutation} I'm AiVA, by Williams Lea. To get started I need both services. ` +
    `${m365Remedy(input)} ${AI_REMEDY}`
  );
}

/**
 * The load state is checked first. While settings are in flight — or after a failed
 * load — neither service is known, so both map to `unknown`. Reporting `offline`
 * there would claim "neither is connected", the unearned certainty this replaces.
 */
export function deriveConnectionState(input: ConnectionStateInput): ConnectionState {
  if (input.settingsStatus !== "ready") return UNKNOWN;

  const { aiConnected, m365Connected } = input;
  const host = input.host ?? null;

  let greeting: string | null;
  if (host === "outlook") greeting = outlookGreeting(input);
  else if (host === "word") greeting = wordGreeting(input);
  else if (host === "powerpoint") greeting = powerPointGreeting(input);
  else greeting = neutralGreeting(input);

  // AIVA-9278: offline shows the "both down" row; the greeting keeps the last-known flags.
  const reachable = input.networkReachable !== false;
  const dotAi = reachable && aiConnected;
  const dotM365 = reachable && m365Connected;

  return {
    dot: dotFor(host, dotAi, dotM365),
    tooltip: tooltipFor(host, dotAi, dotM365),
    greeting,
  };
}
